import { useLoad } from "@redotech/react-util/load";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { SetTaxIdentifiersActionStep } from "@redotech/redo-model/advanced-flow/oms/actions";
import { getTaxIdTypeLabel } from "@redotech/redo-model/outbound-labels/customs";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo, useState } from "react";
import { PartialWithRequiredType } from ".";

export const TaxIdentifiersDropdown = memo(function DutiesPayerRadioGroup({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetTaxIdentifiersActionStep>;
  setStep: (step: PartialWithRequiredType<SetTaxIdentifiersActionStep>) => void;
}) {
  const rpcClient = useContext(RedoOutboundLabelsRpcClientContext);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const taxIdentifiersLoad = useLoad(async () => {
    if (!rpcClient) {
      return [];
    }

    try {
      const { identifiers } = await rpcClient.getTaxIdentifiers(null);
      return identifiers;
    } catch (error) {
      console.error("Failed to load tax identifiers:", error);
    }
    return [];
  }, [rpcClient]);

  const taxIdentifierOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (taxIdentifiersLoad?.value || []).map((taxId) => ({
      id: taxId._id,
      type: "badge",
      value: taxId._id,
      badge: {
        text: `${getTaxIdTypeLabel(taxId.taxIdType)} - ${taxId.nickname}`,
      },
    }));
  }, [taxIdentifiersLoad]);

  const taxIdentifierIds = step.taxIdentifierIds;

  return (
    <Flex flexDirection="column" gap="xs">
      <Text fontSize="xs" textColor="secondary">
        Tax identifiers{" "}
        {taxIdentifierIds ? `(${taxIdentifierIds.length}/8)` : "(0/8)"}
      </Text>
      <RedoTagsDropdownInputButton
        disabled={taxIdentifierIds != null && taxIdentifierIds.length >= 8}
        isDropdownOpen={dropdownOpen}
        onClick={() => {
          if (taxIdentifierIds && taxIdentifierIds.length >= 8) {
            return;
          }
          setDropdownOpen(!dropdownOpen);
        }}
        placeholder={
          taxIdentifierIds && taxIdentifierIds.length >= 8
            ? "Maximum 8 identifiers reached"
            : "Select tax identifiers"
        }
        ref={setDropdownButtonRef}
        removeItem={(item) => {
          setStep({
            ...step,
            taxIdentifierIds: [
              ...(taxIdentifierIds?.filter((id) => id !== item.id) || []),
            ],
          });
        }}
        selectedItems={(taxIdentifierIds || []).reduce<
          BadgeRedoListItem<string>[]
        >((acc, id) => {
          const option = taxIdentifierOptions.find((o) => o.value === id);
          if (option) acc.push(option);
          return acc;
        }, [])}
        size="xs"
      />
      <RedoMultiselectDropdown<string>
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={
          dropdownOpen && !(taxIdentifierIds && taxIdentifierIds.length >= 8)
        }
        fitToAnchor
        items={taxIdentifierOptions}
        itemsLoading={taxIdentifiersLoad.pending}
        selectedItems={(taxIdentifierIds || []).reduce<
          BadgeRedoListItem<string>[]
        >((acc, id) => {
          const option = taxIdentifierOptions.find((o) => o.value === id);
          if (option) acc.push(option);
          return acc;
        }, [])}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          // Limit to 8 tax identifiers
          const limitedItems = items.slice(0, 8);
          setStep({
            ...step,
            taxIdentifierIds: limitedItems.map(({ value }) => value),
          });
        }}
        size="xs"
      />
      {taxIdentifierIds && taxIdentifierIds.length >= 8 && (
        <Text color="error" fontSize="xs">
          Maximum 8 tax identifiers allowed
        </Text>
      )}
    </Flex>
  );
});
